import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import clsx from "clsx";
import { InlineAlertHolder } from "components/Alert";
import { LOCK_HEARTBEAT_INTERVAL_MS } from "constants/outlineGeneration";
import {
  IN_PROGRESS_STATUSES,
  PROPOSAL_OUTLINE_STAGES,
} from "constants/proposals";
import { modalTypes } from "constants/types";
import { useArchivedInlineAlert } from "hooks/useArchivedInlineAlert";
import useLlmHealth from "hooks/useLlmHealth";
import { bool, number, oneOf, oneOfType, string } from "prop-types";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "slices/app/actions/modals";
import { disableNavBarAndSideBar } from "slices/app/appSlice";
import { selectModals } from "slices/app/selectors";
import { selectIsLlmAvailable } from "slices/generativeAi/selectors";
import {
  sendLockHeartbeat,
  unlockEditing,
} from "slices/proposalOutline/actions/sessionActions";
import { reset, setOwner } from "slices/proposalOutline/outlineSlice";
import {
  selectConnectionState,
  selectDocumentIds,
  selectIsAwaitingReqReview,
  selectIsAwaitingTypeReview,
  selectIsEditing,
  selectIsRetryable,
  selectOutlineErrorMessage,
  selectProgressError,
  selectProposalOutlineStatus,
} from "slices/proposalOutline/selectors";

import OutlineGenerationProgress from "./components/OutlineGenerationProgress/OutlineGenerationProgress";
import RequirementsReviewGate from "./gates/RequirementsReviewGate";
import TypeReviewGate from "./gates/TypeReviewGate";
import OutlineTable from "./OutlineTable";
import styles from "./Proposal.module.scss";
import ProposalEmptyState from "./ProposalEmptyState";
import { useProposalOutline } from "./useProposalOutline";

const OWNER_TYPES = ["PROPOSAL", "MISC_ANALYSIS"];
const PAUSED_MESSAGE =
  "Outline generation is paused — a quick question needs your answer.";

const ProposalOutline = ({
  ownerType,
  ownerId,
  ownerName,
  isArchived = false,
}) => {
  const dispatch = useDispatch();
  const outlineStatus = useSelector(selectProposalOutlineStatus);
  const documentIds = useSelector(selectDocumentIds);
  const progressError = useSelector(selectProgressError);
  const isRetryable = useSelector(selectIsRetryable);
  const outlineErrorMessage = useSelector(selectOutlineErrorMessage);
  const isLlmAvailable = useSelector(selectIsLlmAvailable);
  const connectionState = useSelector(selectConnectionState);
  const modals = useSelector(selectModals);
  const isAwaitingTypeReview = useSelector(selectIsAwaitingTypeReview);
  const isAwaitingReqReview = useSelector(selectIsAwaitingReqReview);
  const isEditing = useSelector(selectIsEditing);

  const { checkBeforeAction } = useLlmHealth();

  const isFailed = outlineStatus === PROPOSAL_OUTLINE_STAGES.FAILED;
  const isLostConnection = connectionState === "lost";
  const isAwaitingClarification =
    outlineStatus === PROPOSAL_OUTLINE_STAGES.AWAITING_CLARIFICATION;
  const isClarificationModalOpen = modals.some(
    (modal) => modal.modalType === modalTypes.CLARIFICATION_REQUIRED
  );
  const showPausedBanner = isAwaitingClarification && !isClarificationModalOpen;

  // Read in the unmount cleanup below, which would otherwise close over whatever isEditing was
  // when the effect last ran rather than what it is when the user leaves.
  const isEditingRef = useRef(isEditing);
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (ownerType && ownerId != null) {
      dispatch(setOwner({ ownerType, ownerId, ownerName }));
    }
  }, [dispatch, ownerType, ownerId, ownerName]);

  // Deliberately NOT keyed on ownerName, unlike the effect above. Callers pass an async-resolved
  // title (opportunity?.title, miscAnalysis?.name), so ownerName changes from undefined to its
  // real value a moment after mount. Sharing one effect meant that resolution ran this teardown:
  // reset() wiped the outline slice while useProposalOutline's fetch keys only on
  // [ownerType, ownerId] and so never refetched — a blank pane — and unlockEditing released the
  // lock out from under a user who was still typing.
  useEffect(() => {
    return () => {
      // Leaving mid-edit used to strand the lock for its full TTL, blocking colleagues from an
      // outline nobody is editing, and leave the sidebar masked until a reload.
      if (isEditingRef.current && ownerType && ownerId != null) {
        dispatch(unlockEditing(ownerType, ownerId));
        dispatch(disableNavBarAndSideBar(false));
      }
      // reset, not clearOwner: clearOwner left the sections, coverage and COMPLETED status of the
      // outline just left in the slice, so the next proposal rendered them under its own title —
      // with a live Edit button, and a lock acquired against the new owner.
      dispatch(reset());
    };
  }, [dispatch, ownerType, ownerId]);

  useEffect(() => {
    if (!isEditing || !ownerType || ownerId == null) {
      return undefined;
    }
    const timer = setInterval(() => {
      dispatch(sendLockHeartbeat(ownerType, ownerId));
    }, LOCK_HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [dispatch, isEditing, ownerType, ownerId]);

  const {
    generate,
    regenerate,
    reconnect,
    resume,
    confirmTypes,
    confirmRequirements,
    submitTypeAnswers,
    submitReqAnswers,
    reExtract,
    addRequirement,
    updateRequirement,
    removeRequirement,
    retryFailedStage,
  } = useProposalOutline(ownerType, ownerId, checkBeforeAction);

  useArchivedInlineAlert({ isArchived, label: "proposal outline" });

  // Resume beats regenerate whenever the backend says the failed run still has usable stage
  // output — it skips the stages that already succeeded (extraction above all).
  const onRetry = () => {
    if (isLostConnection) {
      return reconnect();
    }
    if (isRetryable) {
      return retryFailedStage();
    }
    return regenerate(null, documentIds);
  };

  const onAnswerQuestions = () =>
    dispatch(
      showModal(modalTypes.CLARIFICATION_REQUIRED, {
        ownerType,
        ownerId,
        onSubmit: resume,
      })
    );

  // Only FAILED counts as stale here. PENDING is a live queued status on this branch (it is in
  // IN_PROGRESS_STATUSES and the stale reaper flips genuinely-dead rows to FAILED), so it must keep
  // rendering the progress view rather than a blank pane.
  const hasStaleOutline = isFailed;

  const showEmptyState = !outlineStatus || hasStaleOutline;

  return (
    <>
      <div
        className={
          !outlineStatus
            ? "auth_page_content__children__alert_holder"
            : undefined
        }
      >
        <InlineAlertHolder />
      </div>

      {progressError && !outlineStatus && (
        <div className={styles.error}>
          {progressError || "Generation failed"}
        </div>
      )}
      {isFailed && (
        <div className={styles.error} role="alert">
          <p className={styles.error_msg}>
            {outlineErrorMessage || "The previous outline generation failed."}
          </p>
          {isRetryable ? (
            <NewButton onClick={onRetry} variant={NewButtonVariant.l1}>
              Resume Generation
            </NewButton>
          ) : (
            <p className={styles.error_msg}>
              Please select documents and try again.
            </p>
          )}
        </div>
      )}
      {showPausedBanner && (
        <div className={styles.paused_banner} role="alert">
          <p className={styles.paused_banner_msg}>{PAUSED_MESSAGE}</p>
          <NewButton onClick={onAnswerQuestions} variant={NewButtonVariant.l1}>
            Answer Questions
          </NewButton>
        </div>
      )}
      {showEmptyState ? (
        <ProposalEmptyState
          onSubmitDocuments={hasStaleOutline ? regenerate : generate}
          isArchived={isArchived}
        />
      ) : (
        <div
          className={clsx(
            styles.proposal,
            "auth_page_content--disable-padding"
          )}
          data-testid="proposal"
        >
          {IN_PROGRESS_STATUSES.has(outlineStatus) && (
            <OutlineGenerationProgress onRetry={onRetry} />
          )}

          {isAwaitingTypeReview && (
            <TypeReviewGate
              onConfirm={confirmTypes}
              onSubmitAnswers={submitTypeAnswers}
            />
          )}

          {isAwaitingReqReview && (
            <RequirementsReviewGate
              onConfirm={confirmRequirements}
              onSubmitAnswers={submitReqAnswers}
              onAddRequirement={addRequirement}
              onUpdateRequirement={updateRequirement}
              onRemoveRequirement={removeRequirement}
              onReExtract={reExtract}
            />
          )}

          {outlineStatus === PROPOSAL_OUTLINE_STAGES.COMPLETED && (
            <OutlineTable
              onSubmitRegenerate={regenerate}
              llmAvailable={isLlmAvailable}
              isArchived={isArchived}
            />
          )}
        </div>
      )}
    </>
  );
};

ProposalOutline.propTypes = {
  isArchived: bool,
  ownerType: oneOf(OWNER_TYPES).isRequired,
  ownerId: oneOfType([number, string]).isRequired,
  ownerName: string,
};

export default ProposalOutline;
